import React, { useEffect, useState } from 'react'
import { Form, Button, Col, Row, InputGroup, Alert } from 'react-bootstrap'
import { withRouter } from 'react-router'
import * as Yup from 'yup'
import { ErrorMessage, Formik } from 'formik'
import { CardElement, useElements } from '@stripe/react-stripe-js'
import { useStripeWithHandling } from 'hooks/useStripeWithHandling'
import BusinessDetailsModal from './BusinessDetailsModal'

import { transactionFailed, transactionInit, transactionProcessing } from '../../../../Pricing/actions'
import { createPaymentMethod, createSubscription, createFreeTrialSubscription } from '../../../../services/payment'
import { setShowPaymentModal } from 'actions'
import GoogleApplePay from './GoogleApplePay'
import styles from '../../PaymentModal.module.css'
import { withGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { PLANS, PLANS_ID } from '../../../../constants/plans'
import {
  calcUpgradeTime,
  trackSubscriptionUpgrade,
  trackSubscriptionUpgradeInfo,
  trackTrialStart
} from '../../../../utils/analytics'
import { API_ROOT } from '../../../../constants'
import api from 'utils/api'
import * as Sentry from '@sentry/react'
import UPIPay from './UPIPay'
import { initializeCoupon } from '../../../../utils/coupon'
import { getUpgradeReason } from 'utils/general'

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      border: 'solid 1px #333',
      color: '#32325d',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  }
}

const schema = Yup.object({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  email: Yup.string().email('Email is invalid').required('Email is required')
})

const getRedirectUrl = ({ customerId, isTrialUpgrade, discountPercentage, productId }) => {
  let url = `/pro-welcome?id=${customerId}&skipq=${isTrialUpgrade}`

  if (discountPercentage) {
    url += `&percentOff=${discountPercentage}`
  }

  if (productId === PLANS.API.id) {
    url += `&api-welcome=true`
  }

  return url
}

const normalizeBusinessDetails = (details) => {
  if (!details) {
    return null
  }

  const address = Object.entries({
    line1: details.address?.line1,
    line2: details.address?.line2,
    city: details.address?.city,
    state: details.address?.state,
    postal_code: details.address?.postal_code,
    country: details.address?.country
  }).reduce((acc, [key, value]) => {
    if (value) {
      acc[key] = value
    }
    return acc
  }, {})

  const organization = details.organization?.trim() || ''
  const vatId = details.vatId?.trim() || ''

  if (!organization && !vatId && !Object.keys(address).length) {
    return null
  }

  return {
    organization,
    address,
    ...(vatId ? { vatId } : {}),
    ...(details.taxIdType ? { taxIdType: details.taxIdType } : {}),
    ...(details.externalTaxIdType ? { externalTaxIdType: details.externalTaxIdType } : {}),
    isBusinessAccount: true
  }
}

const buildBillingDetails = ({ checkoutEmail, customerName, businessDetails }) => {
  const normalizedBusinessDetails = normalizeBusinessDetails(businessDetails)
  const billingDetails = {
    email: checkoutEmail,
    name: customerName
  }

  if (!normalizedBusinessDetails) {
    return billingDetails
  }

  if (normalizedBusinessDetails.organization) {
    billingDetails.name = normalizedBusinessDetails.organization
  }

  if (Object.keys(normalizedBusinessDetails.address).length) {
    billingDetails.address = normalizedBusinessDetails.address
  }

  return billingDetails
}

const applyBusinessDetailsToSubscriptionParams = (subscriptionParams, businessDetails) => {
  const normalizedBusinessDetails = normalizeBusinessDetails(businessDetails)

  if (!normalizedBusinessDetails) {
    return subscriptionParams
  }

  subscriptionParams.businessDetails = normalizedBusinessDetails

  return subscriptionParams
}

const formatBusinessAddress = (address = {}) =>
  [address.line1, address.line2, address.city, address.state, address.postal_code, address.country]
    .filter(Boolean)
    .join(', ')

const PaymentModal = ({
  className,
  dispatch,
  history,
  priceData,
  email,
  studentData = {},
  source,
  productId,
  period,
  accountCreated,
  userProfile,
  googleReCaptchaProps,
  handleCouponCodeChange,
  errorMessage,
  setErrorMessage,
  extraTeamMembers = 0,
  teamMemberPriceId = null,
  isFreeTrail,
  onFreeTrialSuccess,
  showUpgradeReason = true,
  allowB2C
}) => {
  const stripe = useStripeWithHandling()
  const elements = useElements()
  const isTrialUpgrade = window.location.pathname.includes('manage')

  const priceId = priceData.stripe?.priceId
  const discountCode = localStorage.getItem('discount-code')

  const [isLoading, setIsLoading] = useState(false)
  const [isCouponValid, setIsCouponValid] = useState(false)
  const [showDiscount, setShowDiscount] = useState(false)
  const [couponCode, setCouponCode] = useState(null)
  const [couponPercent, setCouponPercent] = useState(null)
  const [isCardValid, setIsCardValid] = useState(false)
  const [cardError, setCardError] = useState(null)
  const [showBusinessModal, setShowBusinessModal] = useState(false)
  const [businessDetails, setBusinessDetails] = useState(null)

  useEffect(() => {
    // reset coupon
    handleCouponCodeChange(undefined)
    setIsCouponValid(false)
    setCouponPercent(null)
  }, [period])

  useEffect(() => {
    const couponInfo = initializeCoupon(userProfile, period, discountCode)

    if (couponInfo.couponCode) {
      setCouponCode(couponInfo.couponCode)
      setShowDiscount(couponInfo.shouldShowDiscount)
      verifyCoupon(couponInfo.couponCode)
    }
  }, [userProfile?.ucc, period, discountCode])

  const verifyCoupon = async (couponCode) => {
    const headers = {}
    const body = {
      couponId: couponCode
    }
    if (!couponCode) {
      setErrorMessage('Please enter a coupon code')
      return
    }
    try {
      setIsLoading(true)

      const response = await api.post(`${API_ROOT}/v3/subscription/stripe/verify-coupon`, body, headers)
      if (response.data.error) {
        throw response.data
      }
      const newCoupon = response.data
      setIsCouponValid(true)
      setErrorMessage(undefined)
      setCouponPercent(parseFloat(newCoupon.data.percent_off))
      handleCouponCodeChange(newCoupon.data.percent_off)

      if (localStorage) {
        localStorage.removeItem('discount-code')
      }

      return response.data
    } catch {
      setErrorMessage('Invalid coupon code.')
    } finally {
      setIsLoading(false)
    }
  }

  const onSuccess = (customerId, transactionId, isTrialUpgrade, upgradeReason) => {
    dispatch(transactionInit())
    dispatch(setShowPaymentModal({ showPaymentModal: false, upgradePlanId: null }))
    trackSubscriptionUpgrade(
      email,
      PLANS_ID[productId]?.label,
      PLANS_ID[productId]?.prices[period]?.stripe?.usd?.value,
      period,
      transactionId,
      source
    )
    trackSubscriptionUpgradeInfo(email, calcUpgradeTime(accountCreated), upgradeReason)

    isCouponValid
      ? history.push(getRedirectUrl({ customerId, isTrialUpgrade, discountPercentage: couponPercent, productId }))
      : history.push(getRedirectUrl({ customerId, isTrialUpgrade, productId }))
  }

  const handle3DSecure = async (paymentIntent, paymentMethodId, customerId, subscriptionParams) => {
    try {
      const result = await stripe.confirmCardPayment(paymentIntent.client_secret, {
        payment_method: paymentMethodId
      })

      if (result.error) {
        dispatch(transactionFailed({ errorCode: '3D_SECURE_FAILED' }))
        throw new Error('3D_SECURE_FAILED')
      }

      if (result.paymentIntent.status === 'succeeded') {
        onSuccess(customerId, result?.paymentIntent?.id, isTrialUpgrade, subscriptionParams?.upgradeReason)
      }
    } catch {
      dispatch(transactionFailed({ errorCode: '3D_SECURE_FAILED' }))
      throw new Error('3D_SECURE_FAILED')
    }
  }

  const handleOpenBusinessModal = () => {
    setShowBusinessModal(true)
  }

  const handleSaveBusinessDetails = (details) => {
    setBusinessDetails(normalizeBusinessDetails(details))
    setShowBusinessModal(false)
  }

  const processSubscription = (subscriptionParams, paymentMethodId, recaptchaValue) => {
    const headers = {
      'x-recaptcha': recaptchaValue
    }
    createSubscription({
      ...subscriptionParams,
      paymentMethodId,
      couponId: isCouponValid ? couponCode : null,
      headers
    })
      .then(async ({ data }) => {
        //success
        if (data?.subscriptionStatus === 'active') {
          // affiliate tracking
          if (window.fpr) {
            window.fpr('referral', { email: subscriptionParams.email.toLowerCase() })
          }
          onSuccess(data?.customerId, data?.paymentIntent?.id, isTrialUpgrade, subscriptionParams?.upgradeReason)
        } else if (data?.paymentStatus === 'requires_action') {
          //3D Secure
          await handle3DSecure(data?.paymentIntent, paymentMethodId, data?.customerId, subscriptionParams)
        } else {
          dispatch(transactionFailed({ errorCode: 'CREATE_SUBSCRIPTION_FAILED' }))
          throw Error('CREATE_SUBSCRIPTION_FAILED')
        }
      })
      .catch((err) => {
        const error = err?.response?.data || err
        const declineCode = error?.declineCode || error?.message
        const errorCode = error?.errorCode || error?.message

        let accountAlreadyExistsMessage

        if (error?.code === 'DUPLICATE_ACCOUNT_REQUEST') {
          accountAlreadyExistsMessage = error?.message
        }

        dispatch(transactionFailed({ declineCode, errorCode, accountAlreadyExistsMessage }))
      })
  }

  const processFreeTrialSubscription = (subscriptionParams, paymentMethodId, recaptchaValue) => {
    const headers = {
      'x-recaptcha': recaptchaValue
    }

    createFreeTrialSubscription({
      email: subscriptionParams.email,
      name: subscriptionParams.name,
      paymentMethodId,
      priceId: subscriptionParams.priceId,
      allowB2C,
      businessDetails: subscriptionParams.businessDetails,
      headers
    })
      .then(({ data }) => {
        trackTrialStart(allowB2C ? 'solo' : 'pro', subscriptionParams?.email)
        onFreeTrialSuccess(data)
      })
      .catch((err) => {
        if (err.error === 'TRIAL_ALREADY_USED') {
          localStorage.setItem('hasCompletedFreeTrial', 'true')
        }
        const error = err?.response?.data || err
        const errorCode = error?.error || error?.errorCode
        const declineCode = error?.declineCode

        dispatch(
          transactionFailed({
            errorCode: errorCode,
            declineCode: declineCode
          })
        )

        // Sentry.captureException(err, {
        //   tags: {
        //     section: 'free-trial-subscription-error'
        //   }
        // })
      })
  }

  const processCardTransaction = async (subscriptionParams, cardElement, billingDetails, recaptchaValue) => {
    try {
      const { error, paymentMethod } = await createPaymentMethod(stripe, cardElement, billingDetails)

      if (error) {
        dispatch(transactionFailed({ errorCode: `CPM_FAILED - ${error.type}` }))
        Sentry.captureException(error, {
          tags: {
            section: 'payment-process-card-error'
          }
        })
      } else {
        if (isFreeTrail) {
          processFreeTrialSubscription(subscriptionParams, paymentMethod.id, recaptchaValue)
        } else {
          processSubscription(subscriptionParams, paymentMethod.id, recaptchaValue)
        }
      }
    } catch (error) {
      dispatch(transactionFailed({ errorCode: 'PAYMENT_METHOD_CREATION_FAILED' }))
      Sentry.captureException(error, {
        tags: {
          section: 'payment-method-creation-error'
        }
      })
    }
  }

  const processAppleGooglePayTransaction = async (subscriptionParams, paymentMethodId) => {
    dispatch(transactionProcessing())
    const recaptchaValue = await googleReCaptchaProps.executeRecaptcha('payment')

    applyBusinessDetailsToSubscriptionParams(subscriptionParams, businessDetails)

    processSubscription(subscriptionParams, paymentMethodId, recaptchaValue)
  }

  const onSubmit = async (formData) => {
    if (!isCardValid) {
      setCardError('Please complete your card information')
      return
    }

    dispatch(transactionProcessing())
    const recaptchaValue = await googleReCaptchaProps.executeRecaptcha('payment')

    const fullName = `${formData.firstName} ${formData.lastName}`.trim()
    const checkoutEmail = formData.email || email || studentData.email

    const billingDetails = buildBillingDetails({
      checkoutEmail,
      customerName: fullName,
      businessDetails
    })

    const subscriptionParams = {
      ...formData,
      name: fullName,
      email: checkoutEmail,
      priceId,
      productId,
      paymentGateway: 'STRIPE',
      firstName: formData.firstName,
      lastName: formData.lastName
    }

    if (extraTeamMembers > 0 && teamMemberPriceId) {
      subscriptionParams.teamMemberPriceId = teamMemberPriceId
    }

    if (isCouponValid) {
      subscriptionParams.couponCode = couponCode
    }

    applyBusinessDetailsToSubscriptionParams(subscriptionParams, businessDetails)

    const reason = getUpgradeReason()
    subscriptionParams.upgradeReason = formData.upgradeReason || reason || 'N/A'

    await processCardTransaction(subscriptionParams, elements.getElement(CardElement), billingDetails, recaptchaValue)
  }

  const handleCardChange = (event) => {
    setIsCardValid(event.complete)
    if (event.error) {
      setCardError(event.error.message)
    } else {
      setCardError(null)
    }
  }

  return (
    <Formik
      validationSchema={schema}
      onSubmit={onSubmit}
      initialValues={{
        firstName: '',
        lastName: '',
        email: email || studentData.email || '',
        upgradeReason: ''
      }}
    >
      {({ handleSubmit, handleChange, values }) => (
        <>
          <form className={className} onSubmit={handleSubmit}>
            {!isFreeTrail && <UPIPay payProId={priceData.payPro?.priceId} email={email} />}
            {!isFreeTrail && (
              <GoogleApplePay
                priceData={priceData}
                productId={productId}
                processTransaction={processAppleGooglePayTransaction}
                email={email}
              />
            )}
            <Row>
              <Col>
                <Form.Control
                  name="firstName"
                  type="text"
                  placeholder="First name"
                  value={values.firstName}
                  onChange={handleChange}
                />
                <ErrorMessage name="firstName" component="div" className="validation-error" />
              </Col>
              <Col>
                <Form.Control
                  name="lastName"
                  type="text"
                  placeholder="Last name"
                  value={values.lastName}
                  onChange={handleChange}
                />
                <ErrorMessage name="lastName" component="div" className="validation-error" />
              </Col>
            </Row>
            {!email && (
              <Form.Control
                className="mt-2"
                name="email"
                type="email"
                placeholder="Email"
                value={values.email}
                onChange={handleChange}
              />
            )}
            <ErrorMessage name="email" component="div" className="validation-error" />
            <div data-testid="StripePay-card-element">
              <CardElement
                className={styles.StripeElement}
                options={CARD_ELEMENT_OPTIONS}
                onChange={handleCardChange}
              />
            </div>
            <div className="p-1" style={{ minHeight: 30 }}>
              <div className="opacity-50 font-14 float-start">
                Secured with{' '}
                <a href="https://stripe.com" target="_blank" rel="noreferrer">
                  <img src="/assets/icons/stripe.svg" height="30" alt="Stripe" />
                </a>
              </div>
              {showDiscount ? (
                <>
                  <InputGroup className="mt-2">
                    <Form.Control
                      className=""
                      placeholder="Enter coupon code"
                      aria-label="discount"
                      name="couponCode"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      onClick={() => setErrorMessage('')}
                      disabled={isLoading}
                    />

                    <Button size="sm" onClick={() => verifyCoupon(couponCode)} disabled={isLoading}>
                      {isLoading ? 'Applying...' : 'Apply'}
                    </Button>
                  </InputGroup>
                  {errorMessage && <span className="text-danger">{errorMessage}</span>}
                </>
              ) : (
                <div className="font-small float-end mt-1">
                  {!discountCode && !isFreeTrail && (
                    <u
                      className={styles.apply}
                      onClick={() => {
                        setShowDiscount(true)
                      }}
                    >
                      Apply discount code
                    </u>
                  )}
                </div>
              )}
            </div>
            {cardError && (
              <Alert variant="danger" className="mt-2">
                {cardError}
              </Alert>
            )}

            <>
              <div className="d-flex align-items-center mt-3 mb-3">
                <Button
                  variant="link"
                  size="sm"
                  type="button"
                  onClick={handleOpenBusinessModal}
                  className="d-flex align-items-center paying_business p-0"
                >
                  <i className="bi bi-building me-2"></i>
                  {businessDetails ? 'Edit business details' : 'Add tax / business details'}
                </Button>

                {businessDetails && (
                  <div className="ms-3 text-success">
                    <i className="bi bi-check-circle me-1"></i>
                    Business details added
                  </div>
                )}
              </div>

              {businessDetails && (
                <div className="business-details-summary p-2 mb-3 bg-light round-8 p-3">
                  <div className="mb-2 text-primary">
                    <i className="bi bi-receipt me-1"></i>
                    <small>
                      <strong>The following details will appear on your invoice:</strong>
                    </small>
                  </div>
                  {businessDetails.organization && (
                    <small className="d-block text-muted">
                      <strong>Organization:</strong> {businessDetails.organization}
                    </small>
                  )}
                  {businessDetails.vatId && (
                    <small className="d-block text-muted">
                      <strong>VAT/Tax ID:</strong> {businessDetails.vatId}
                    </small>
                  )}
                  {Object.keys(businessDetails.address || {}).length > 0 && (
                    <small className="d-block text-muted">
                      <strong>Address:</strong> {formatBusinessAddress(businessDetails.address)}
                    </small>
                  )}
                </div>
              )}
            </>

            {showUpgradeReason && (
              <Form.Control
                name="upgradeReason"
                className="mt-3"
                as="select"
                value={values.upgradeReason}
                onChange={handleChange}
              >
                <option value="">Reason for upgrade...</option>
                <option value="custom-domain">Link a custom domain</option>
                <option value="more-links">Upload more projects</option>
                <option value="file-size">Upload a larger file</option>
                <option value="analytics">Analytics</option>
                <option value="editing">Editing functionalities</option>
                <option value="no-ads">Remove Tiiny Host branding</option>
                <option value="other">Other</option>
              </Form.Control>
            )}

            <div className="w-100">
              <Button
                className="w-100 mt-3 tr-stripe-pay"
                type="submit"
                disabled={!stripe}
                data-testid="StripePay-pay-button"
              >
                {isFreeTrail ? `Start your 7 day free trial` : 'Pay'}
              </Button>
            </div>
          </form>

          <BusinessDetailsModal
            show={showBusinessModal}
            onHide={() => setShowBusinessModal(false)}
            onSave={handleSaveBusinessDetails}
            initialValues={businessDetails || {}}
          />
        </>
      )}
    </Formik>
  )
}

export default withRouter(withGoogleReCaptcha(PaymentModal))
